/**
 * News Page
 *
 * Main news list page with grid layout, category filtering, and sorting
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  SimpleGrid,
  Tabs,
  Group,
  SegmentedControl,
  Paper,
  Select,
} from '@mantine/core';
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import { Layout } from '../../shared/components';
import { useSEO } from '../../shared/hooks';
import { NewsCard } from './components/News';
import { NEWS_ITEMS } from '../../data/news/newsData';
import type { NewsCategory } from '../../data/news/newsTypes';

/**
 * Group news items by year and month
 */
interface NewsGroup {
  year: number;
  month: number;
  monthName: string;
  items: typeof NEWS_ITEMS;
}

/**
 * News Page Component
 *
 * Displays all news items in a grid layout with filtering and sorting
 */
export function News() {
  useSEO({
    title: 'Aktualności - Wiadomości energetyczne',
    description: 'Aktualności i informacje o rynku energii w Polsce. Zmiany taryf, regulacje URE, porady oszczędzania.',
  });

  const [selectedCategory, setSelectedCategory] = useState<NewsCategory | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  // Filter news by category
  const filteredNews = useMemo(() => {
    let items = [...NEWS_ITEMS];

    // Filter by category
    if (selectedCategory !== 'all') {
      items = items.filter((item) => item.category === selectedCategory);
    }

    // Sort by date
    items.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    return items;
  }, [selectedCategory, sortOrder]);

  // Group news by year and month
  const groupedNews = useMemo(() => {
    const groups: NewsGroup[] = [];

    filteredNews.forEach((item) => {
      const date = new Date(item.date);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthName = date.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });

      // Find existing group
      let group = groups.find((g) => g.year === year && g.month === month);

      if (!group) {
        group = { year, month, monthName, items: [] };
        groups.push(group);
      }

      group.items.push(item);
    });

    return groups;
  }, [filteredNews]);

  return (
    <Layout isAuthenticated={false}>
      <Container size="lg" py={80}>
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Title order={1} mb="md">
              Aktualności
            </Title>
            <Text size="lg" c="dimmed">
              Najnowsze informacje o platformie TaryfyPrądu.pl - nowe funkcje, aktualizacje i ogłoszenia
            </Text>
          </div>

          {/* Filters Section */}
          <Paper withBorder p="md" radius="md">
            <Stack gap="md">
              {/* Header Row: Category Label + Sort Control */}
              <Group justify="space-between" align="center" wrap="wrap">
                <Text fw={500}>
                  Filtruj według kategorii:
                </Text>

                {/* Sort Order */}
                <Group gap="xs" align="center">
                  <Text fw={500}>Sortuj:</Text>
                  {/* Mobile: Select dropdown with icon */}
                  <Select
                    value={sortOrder}
                    onChange={(value) => setSortOrder(value as 'newest' | 'oldest')}
                    data={[
                      {
                        label: 'Najnowsze pierwsze',
                        value: 'newest',
                      },
                      {
                        label: 'Najstarsze pierwsze',
                        value: 'oldest',
                      },
                    ]}
                    w={200}
                    leftSection={sortOrder === 'newest' ? <IconArrowDown size={16} /> : <IconArrowUp size={16} />}
                    comboboxProps={{ shadow: 'md' }}
                    hiddenFrom="sm"
                  />
                  {/* Desktop: Segmented control */}
                  <SegmentedControl
                    value={sortOrder}
                    onChange={(value) => setSortOrder(value as 'newest' | 'oldest')}
                    data={[
                      { label: 'Najnowsze', value: 'newest' },
                      { label: 'Najstarsze', value: 'oldest' },
                    ]}
                    visibleFrom="sm"
                  />
                </Group>
              </Group>

              {/* Category Filters Tabs */}
              <Tabs
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value as NewsCategory | 'all')}
                variant="pills"
              >
                <Tabs.List>
                  <Tabs.Tab value="all">Wszystkie</Tabs.Tab>
                  <Tabs.Tab value="feature">Nowości</Tabs.Tab>
                  <Tabs.Tab value="update">Aktualizacje</Tabs.Tab>
                  <Tabs.Tab value="announcement">Ogłoszenia</Tabs.Tab>
                  <Tabs.Tab value="maintenance">Konserwacje</Tabs.Tab>
                </Tabs.List>
              </Tabs>
            </Stack>
          </Paper>

          {/* News Grid - Grouped by Month */}
          {groupedNews.length === 0 ? (
            <Paper withBorder p="xl" radius="md">
              <Text ta="center" c="dimmed">
                Brak aktualności w tej kategorii
              </Text>
            </Paper>
          ) : (
            groupedNews.map((group) => (
              <Stack key={`${group.year}-${group.month}`} gap="md">
                {/* Month Header */}
                <Title order={2} size="h3" c="dimmed" tt="capitalize">
                  {group.monthName}
                </Title>

                {/* News Grid */}
                <SimpleGrid
                  cols={{ base: 1, sm: 2, md: 3 }}
                  spacing="lg"
                  style={{
                    gridAutoFlow: 'dense',
                  }}
                >
                  {group.items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        gridColumn: item.featured ? 'span 2' : 'span 1',
                      }}
                    >
                      <NewsCard item={item} featured={item.featured} showCTA />
                    </div>
                  ))}
                </SimpleGrid>
              </Stack>
            ))
          )}
        </Stack>
      </Container>
    </Layout>
  );
}
